import { useCallback, useState, useRef } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { SSE } from 'sse.js';
import { v4 } from 'uuid';
import { CHAT_BOT } from 'queries/query-keys';
import { toastVariant, useToast } from '__components/ToastProvider';
import { MessageType } from 'ts-components/Chat';
import { ChatMessageDataStreaming } from 'ts-components/ChatBot/types';
import { getAnswersOrAlertsByMessage } from 'ts-components/ChatBot/utils';
import { monitoringExceptionHelper } from 'instrumentation/helpers';

const DEFAULT_EVENT_LISTENER = {
  answer_mode: true,
  user_output: true,
  alert: true,
  refining: true,
  question: true,
  answer: true,
  final: true,
};

export type UseChatBotEventStreamOptions = {
  chatSessionId: string;
  eventListenerNames: string[];
  generationVarToMessageHeaderMapper?: Record<string, string>;
  onFinal?: () => void;
  onInitialMessageLoading?: () => void;
};

export type StreamingEndpointConfig = {
  getStreamingEndpoint: (id: string) => string;
  getRetryStreamingEndpoint?: (id: string) => string;
};

export const useChatBotEventStream = ({
  chatSessionId = '',
  eventListenerNames,
  generationVarToMessageHeaderMapper = {},
  onFinal: onFinalCallback,
  onInitialMessageLoading,
}: UseChatBotEventStreamOptions) => {
  const queryClient = useQueryClient();
  const { t } = useTranslation();
  const [showToast] = useToast();
  const [isStreaming, setIsStreaming] = useState(false);
  const [isStreamingCustomEvent, setIsStreamingCustomEvent] = useState(false);
  const eventSourceRef = useRef<SSE | null>(null);
  const isAbortedRef = useRef(false);
  const activeSessionIdRef = useRef<string>('');

  const abortStream = useCallback(() => {
    isAbortedRef.current = true;
    const sessionToClean = activeSessionIdRef.current;

    if (eventSourceRef.current) {
      try {
        eventSourceRef.current.close();
      } catch (error) {
        monitoringExceptionHelper(error);
      }
      eventSourceRef.current = null;
    }
    setIsStreaming(false);
    setIsStreamingCustomEvent(false);

    if (sessionToClean) {
      queryClient.setQueryData(
        CHAT_BOT.CHAT_SESSION(sessionToClean),
        (prev: { messages: MessageType[] } = { messages: [] }) => {
          if (!prev?.messages) return prev;
          const newMessages = prev.messages.filter(
            (msg) => !(msg?.isResponseRefining || msg?.isResponseLoading)
          );
          newMessages.forEach((msg) => {
            if (msg?.streaming) {
              msg.streaming = false;
              msg.isDoneStreaming = true;
            }
          });
          return {
            ...prev,
            messages: newMessages,
          };
        }
      );
    }
  }, [queryClient]);

  const buildMessageHeader = useCallback(
    (generationVar: string) => {
      if (!generationVar) return '';
      else if (generationVarToMessageHeaderMapper[generationVar]) {
        return generationVarToMessageHeaderMapper[generationVar];
      }
      const captializedHeader = generationVar
        .split('_')
        .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
        .join(' ');
      return captializedHeader;
    },
    [generationVarToMessageHeaderMapper]
  );

  const dropTrailingPlaceholders = (
    arr: MessageType[],
    maxToDrop = 2
  ): MessageType[] => {
    if (arr.length === 0) return arr;
    let end = arr.length;
    let dropped = 0;
    while (end > 0 && dropped < maxToDrop) {
      const m = arr[end - 1];
      if (m?.isResponseRefining || m?.isResponseLoading) {
        end--;
        dropped++;
      } else {
        break;
      }
    }
    return end === arr.length ? arr : arr.slice(0, end);
  };

  const reverseFindIndex = (
    arr: MessageType[],
    pred: (m: MessageType, idx: number) => boolean
  ) => {
    for (let i = arr.length - 1; i >= 0; i--) {
      if (pred(arr[i], i)) return i;
    }
    return -1;
  };

  const initiateStream = useCallback(
    async ({
      endpoint,
      payload = {},
      method = 'POST',
      onMessage,
      onRefine,
      onFinal,
      onError,
      onInitialSend,
    }: {
      endpoint: string;
      payload?: any;
      method?: 'POST' | 'GET';
      onMessage: (data: string, eventName?: string) => void;
      onRefine: () => void;
      onFinal: (data: ChatMessageDataStreaming) => void;
      onError: (error: any) => void;
      onInitialSend: () => void;
    }) => {
      try {
        isAbortedRef.current = false;
        setIsStreaming(true);
        onInitialSend();

        if (eventSourceRef.current) {
          try {
            eventSourceRef.current.close();
          } catch (error) {
            monitoringExceptionHelper(error);
          }
        }

        const eventSource = new SSE(endpoint, {
          payload:
            Object.keys(payload).length > 0
              ? JSON.stringify(payload)
              : undefined,
          headers: {
            'Content-Type': 'application/json',
          },
          method,
          withCredentials: true,
        });

        eventSourceRef.current = eventSource;
        const allEventListeners = [
          ...new Set([
            ...eventListenerNames,
            ...Object.keys(DEFAULT_EVENT_LISTENER),
          ]),
        ].filter((eventName) => eventName !== 'final');

        allEventListeners?.forEach((eventName) => {
          eventSource.addEventListener(eventName, (event: any) => {
            if (isAbortedRef.current) return;
            try {
              if (event.data) {
                switch (eventName) {
                  case 'refining':
                    onRefine();
                    break;
                  case 'question':
                    onMessage(event.data, eventName);
                    break;
                  case 'alert':
                    onMessage(event.data, eventName);
                    break;
                  case 'user_output':
                    onMessage(event.data, eventName);
                    break;
                  default:
                    onMessage(event.data, eventName);
                    break;
                }
              }
            } catch (error) {
              monitoringExceptionHelper(error);
            }
          });
        });

        eventSource.addEventListener('final', (event: any) => {
          if (isAbortedRef.current) {
            eventSourceRef.current = null;
            return;
          }
          try {
            if (event.data) {
              onFinal(JSON.parse(event.data));
            }
          } catch (error) {
            monitoringExceptionHelper(error);
          } finally {
            setIsStreaming(false);
            eventSourceRef.current = null;
          }
        });

        eventSource.onerror = (event: any) => {
          if (isAbortedRef.current) {
            eventSourceRef.current = null;
            return;
          }
          if (onError) {
            onError(event);
          }
        };
      } catch (error) {
        monitoringExceptionHelper(error);
        if (onError) {
          onError(error);
        }
      }
    },
    [eventListenerNames]
  );

  const sendMessageWithEventStream = useCallback(
    async (
      message: string,
      endpointConfig: StreamingEndpointConfig,
      sessionId: string = '',
      isRetry = false
    ) => {
      const onInitialSend = () => {
        queryClient.setQueryData(
          CHAT_BOT.CHAT_SESSION(chatSessionId),
          (prev: { messages: MessageType[] } = { messages: [] }) => ({
            ...prev,
            answers: [],
            messages: [
              ...prev.messages,
              ...(isRetry
                ? []
                : [
                    {
                      id: v4(),
                      messageContent: message,
                      isUserMessage: true,
                      isResponseLoading: false,
                      isCollapsable: false,
                    },
                  ]),
              {
                id: v4(),
                messageContent: '',
                isUserMessage: false,
                isResponseLoading: true,
                isCollapsable: false,
              },
            ],
          })
        );

        // Call the callback once after setQueryData
        onInitialMessageLoading?.();
      };

      const onMessage = (data: string, eventName?: string) => {
        const isCustomEvent =
          !DEFAULT_EVENT_LISTENER[
            eventName as keyof typeof DEFAULT_EVENT_LISTENER
          ];

        let generationVar = '';
        if (isCustomEvent) {
          generationVar = eventName ?? '';
          setIsStreamingCustomEvent(true);
        }

        const newChunk = JSON.parse(data);

        queryClient.setQueryData(
          CHAT_BOT.CHAT_SESSION(chatSessionId),
          (prev: { messages: MessageType[] } = { messages: [] }) => {
            const prevMessages = prev.messages ?? [];
            if (prevMessages.length === 0) return prev;
            const messagesWithoutLoaders = dropTrailingPlaceholders(
              prevMessages,
              2
            );
            const messages = messagesWithoutLoaders.length
              ? messagesWithoutLoaders
              : prevMessages;
            const lastMessage = messages[messages.length - 1];
            const lastSystem = lastMessage?.isResponseRefining
              ? messages[messages.length - 2]
              : lastMessage;

            const lastMessageGenerationVar = (
              messages[messages.length - 1]?.id ?? ''
            ).split('-')[0];

            const isLastMessageGenerationVar =
              eventListenerNames.includes(lastMessageGenerationVar) ||
              DEFAULT_EVENT_LISTENER[
                lastMessageGenerationVar as keyof typeof DEFAULT_EVENT_LISTENER
              ];

            const shouldUpdateLastMessage = Boolean(
              isLastMessageGenerationVar
                ? lastMessageGenerationVar === generationVar
                : messages[messages.length - 1]?.eventName === eventName
            );

            if (!shouldUpdateLastMessage) {
              messages[messages.length - 1].isResponseLoading = false;
              messages[messages.length - 1].streaming = false;
              messages[messages.length - 1].isDoneStreaming = true;
            }

            // guard for user_output placement
            if (
              eventName === 'user_output' &&
              lastSystem &&
              !lastSystem.isUserMessage &&
              lastSystem.eventName !== 'refining' &&
              lastSystem.eventName !== 'user_output' &&
              !lastSystem.isResponseLoading &&
              !lastSystem.isResponseRefining
            ) {
              return undefined;
            }

            const messageHeader = buildMessageHeader(generationVar);
            const appendedContent = shouldUpdateLastMessage
              ? (messages[messages.length - 1]?.messageContent ?? '') + newChunk
              : newChunk;

            const newMessage: MessageType = {
              id: generationVar ? `${generationVar}-${v4()}` : `${v4()}`,
              messageContent: appendedContent,
              eventName,
              isUserMessage: false,
              isResponseLoading: false,
              header: messageHeader,
              streaming: true,
              isDoneStreaming: false,
              showFeedbackIcons: true,
              role: 'assistant',
            };

            let newMessages: MessageType[];
            if (shouldUpdateLastMessage && generationVar) {
              newMessages = messages.slice();
              const messageIndex = reverseFindIndex(newMessages, (m) =>
                Boolean(
                  m.streaming &&
                    typeof m.id === 'string' &&
                    m.id.includes(generationVar)
                )
              );

              if (messageIndex !== -1) {
                newMessages[messageIndex] = newMessage;
              } else {
                newMessages.push(newMessage);
              }
            } else if (shouldUpdateLastMessage) {
              if (!messages.length) {
                newMessages = [newMessage];
              } else {
                newMessages = [...messages.slice(0, -1), newMessage];
              }
            } else {
              newMessages = [...messages, newMessage];
            }

            return {
              ...prev,
              answers: [],
              messages: newMessages,
            };
          }
        );
      };

      const onRefine = () => {
        queryClient.setQueryData(
          CHAT_BOT.CHAT_SESSION(chatSessionId),
          (prev: { messages: MessageType[] } = { messages: [] }) => {
            if (prev.messages[prev.messages.length - 1]?.isResponseRefining) {
              return prev;
            }
            const prevMessages = dropTrailingPlaceholders(prev.messages, 2);
            const updatedMessages = [
              ...prevMessages,
              {
                id: v4(),
                messageContent: '',
                isUserMessage: false,
                isResponseRefining: true,
                streaming: false,
                isResponseLoading: false,
                awaitingFinal: prev.messages[prev.messages.length - 2]?.header
                  ? true
                  : false,
              },
            ];
            return {
              ...prev,
              answers: [],
              messages: updatedMessages,
            };
          }
        );
      };

      const onFinal = (data: ChatMessageDataStreaming) => {
        setIsStreamingCustomEvent(false);
        const { ready_for_action, answer_mode, ...rest } = data;
        const { answers, alert } = getAnswersOrAlertsByMessage(data);
        queryClient.setQueryData(
          CHAT_BOT.CHAT_SESSION(chatSessionId),
          (
            prev: { messages: MessageType[]; last_flow: any } = {
              messages: [],
              last_flow: null,
            }
          ) => {
            const allLastSystemMessages = [];
            for (let i = prev.messages.length - 1; i >= 0; i--) {
              const m = prev.messages[i];
              if (m?.isUserMessage === false) {
                allLastSystemMessages.push(m);
              } else {
                break;
              }
            }
            const previousGenerationTypes = allLastSystemMessages
              .map((m) => m.eventName)
              .filter(Boolean);

            const generationVars = Object.keys(rest).filter(
              (key) =>
                !key.includes('json') &&
                !Object.prototype.hasOwnProperty.call(
                  DEFAULT_EVENT_LISTENER,
                  key
                ) &&
                previousGenerationTypes.includes(key) === false
            );

            const generationVarMessagesApplied = [] as string[];
            // Apply any missing generation var messages to the first refining/loading placeholder we find, then remove any extra refining/loading states
            for (let i = 0; i < prev.messages.length; i++) {
              const msg = prev.messages[i];
              if (msg?.isResponseRefining || msg?.isResponseLoading) {
                if (msg.messageContent === '' && generationVars.length > 0) {
                  generationVars.forEach((gv) => {
                    if (!generationVarMessagesApplied.includes(gv)) {
                      const messageHeader = buildMessageHeader(gv);

                      const newMessage = {
                        id: `${gv}-${v4()}`,
                        messageContent:
                          (rest as Record<string, string | string[]>)[gv] ?? '',
                        eventName: gv,
                        isUserMessage: false,
                        isResponseLoading: false,
                        header: messageHeader,
                        streaming: false,
                        isDoneStreaming: true,
                        role: 'assistant',
                      } as MessageType;

                      generationVarMessagesApplied.push(gv);
                      prev.messages[i] = newMessage;
                    }
                  });
                } else {
                  // remove extra loading messages
                  prev.messages.splice(i, 1);
                  i--;
                  continue;
                }
                continue;
              }
              if (msg?.streaming) {
                prev.messages[i].streaming = false;
                prev.messages[i].awaitingFinal = false;
                prev.messages[i].role = 'assistant';
              }
            }
            onFinalCallback?.();
            return {
              ...prev,
              answers,
              answer_mode,
              alert,
              ready_for_action,
              last_flow: ready_for_action ? null : prev.last_flow,
            };
          }
        );
      };

      const onError = () => {
        if (isAbortedRef.current) return;

        showToast({
          message: t(
            'An error occurred while calling our agent, please try again'
          ),
          variant: toastVariant.FAILURE,
        });

        queryClient.setQueryData(
          CHAT_BOT.CHAT_SESSION(chatSessionId),
          (prev: { messages: MessageType[] } = { messages: [] }) => {
            const newMessages = prev.messages.filter(
              (msg) => !(msg?.isResponseRefining || msg?.isResponseLoading)
            );
            return {
              ...prev,
              messages: newMessages,
            };
          }
        );
        setIsStreaming(false);
      };

      const endpoint =
        isRetry && endpointConfig.getRetryStreamingEndpoint
          ? endpointConfig.getRetryStreamingEndpoint(sessionId)
          : endpointConfig.getStreamingEndpoint(sessionId);

      const payload = isRetry ? {} : { text: message };

      return await initiateStream({
        endpoint,
        payload,
        method: 'POST',
        onInitialSend,
        onMessage,
        onFinal,
        onRefine,
        onError,
      });
    },
    [
      onFinalCallback,
      onInitialMessageLoading,
      chatSessionId,
      queryClient,
      initiateStream,
      showToast,
      t,
      buildMessageHeader,
      eventListenerNames,
    ]
  );

  return {
    sendMessageWithEventStream,
    isStreaming,
    isStreamingCustomEvent,
    abortStream,
  };
};
